import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../components/ui/Header';
import MetricCard from './components/MetricCard';
import LoadProgressionChart from './components/LoadProgressionChart';
import DashboardControls from './components/DashboardControls';
import SessionHistoryTable from './components/SessionHistoryTable';
import EditSessionModal from './components/EditSessionModal';
import ContactButton from '../../components/ContactButton';
import Button from '../../components/ui/Button';
import DateRangeSelector from '../../components/ui/DateRangeSelector';

import SessionForm from '../log-session/components/SessionForm';
import { sessionService } from '../../services/sessionService';
import { wellnessService } from '../../services/wellnessService';
import { useAuth } from '../../contexts/AuthContext';
import { generateTrainingLoadChartData, calculateCurrentLoadStats, calculateLoadRatioTrend, calculateReadinessIndex, calculateReadinessForDateRange, calculateDailyLoads } from '../../utils/trainingLoadCalculations';
import { Helmet } from 'react-helmet';
import DownloadReportModal from './components/DownloadReportModal';
import ReportLayout from './components/ReportLayout';
import { generatePDFReport } from '../../utils/pdfGenerator';

const DashboardOverview = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    endDate: new Date()?.toISOString()?.split('T')?.[0],
    preset: '30'
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentLoadStats, setCurrentLoadStats] = useState(null);
  const [loadRatioTrendData, setLoadRatioTrendData] = useState(null);
  const [notification, setNotification] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [isChartRefreshing, setIsChartRefreshing] = useState(false);
  const [wellnessData, setWellnessData] = useState(null);
  const [isLoadingWellness, setIsLoadingWellness] = useState(false);
  const [wellnessTrend, setWellnessTrend] = useState(null);
  const [readinessIndexData, setReadinessIndexData] = useState(null);
  const [isLoadingReadiness, setIsLoadingReadiness] = useState(false);
  const [wellnessChartData, setWellnessChartData] = useState([]);
  const [readinessChartData, setReadinessChartData] = useState([]);
  const [readinessMap, setReadinessMap] = useState({});
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [reportData, setReportData] = useState(null);

  const { user, loading: authLoading } = useAuth();

  // Mock data for dashboard metrics
  const mockMetrics = {
    acuteLoad: { value: 285, trend: 12.5, status: 'amber' },
    chronicLoad: { value: 320, trend: -2.1, status: 'green' },
    loadRatio: { value: 0.89, trend: 15.2, status: 'amber' },
    wellnessScore: { value: 5.2, trend: -8.3, status: 'green' }
  };

  // Mock data for load progression chart
  const mockChartData = [
    { date: '05/11', acuteLoad: 280, chronicLoad: 310, loadRatio: 0.90 },
    { date: '06/11', acuteLoad: 295, chronicLoad: 315, loadRatio: 0.94 },
    { date: '07/11', acuteLoad: 310, chronicLoad: 318, loadRatio: 0.97 },
    { date: '08/11', acuteLoad: 285, chronicLoad: 320, loadRatio: 0.89 },
    { date: '09/11', acuteLoad: 275, chronicLoad: 322, loadRatio: 0.85 },
    { date: '10/11', acuteLoad: 290, chronicLoad: 320, loadRatio: 0.91 },
    { date: '11/11', acuteLoad: 285, chronicLoad: 320, loadRatio: 0.89 }
  ];

  // Mock data for recent sessions with updated structure
  const mockSessions = [
    {
      id: 1,
      date: '2025-11-11',
      sport: 'Running',
      duration: 90,
      activeDuration: 75,
      rpe: 7,
      load: 525,
      notes: 'Long run with intervals, felt strong throughout'
    },
    {
      id: 2,
      date: '2025-11-10',
      sport: 'Cycling',
      duration: 120,
      activeDuration: 90,
      rpe: 6,
      load: 540,
      notes: 'Endurance ride on hilly course'
    },
    {
      id: 3,
      date: '2025-11-09',
      sport: 'Swimming',
      duration: 75,
      activeDuration: 60,
      rpe: 5,
      load: 300,
      notes: 'Technique focus session with coach'
    },
    {
      id: 4,
      date: '2025-11-08',
      sport: 'Running',
      duration: 45,
      activeDuration: 30,
      rpe: 8,
      load: 240,
      notes: 'Speed work - 8x400m intervals'
    },
    {
      id: 5,
      date: '2025-11-07',
      sport: 'Strength',
      duration: 90,
      activeDuration: 75,
      rpe: 6,
      load: 450,
      notes: 'Full body strength session'
    }
  ];

  // Mock wellness data
  const mockWellnessData = {
    sleep: 5.5,
    fatigue: 4.8,
    soreness: 5.0,
    stress: 5.5
  };

  // Mock current load statistics
  const mockLoadStats = {
    acuteLoad: 2055, // 7-day total based on updated sessions
    chronicLoad: 2680, // 28-day average
    currentRatio: 0.77
  };

  useEffect(() => {
    // Load sessions from Supabase instead of mock data
    if (!authLoading && user) {
      loadSessionsFromDatabase();
      loadTodayWellnessData();
      loadReadinessIndexData(); // NEW: Load readiness index
    }
  }, [user, authLoading]);

  // Enhanced load sessions function with live chart updates
  const loadSessionsFromDatabase = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setIsChartRefreshing(true);
    try {
      const data = await sessionService?.getAll();
      setSessions(data || []);
      
      // Calculate stats with real data using new utilities
      if (data && data?.length > 0) {
        const newStats = calculateCurrentLoadStats(data);
        setCurrentLoadStats(newStats);
        
        // Calculate load ratio trend data
        const trendData = calculateLoadRatioTrend(data);
        setLoadRatioTrendData(trendData);
        
        // Generate live chart data
        const chartDataPoints = generateTrainingLoadChartData(
          data, 
          parseInt(dateRange?.preset) || 30,
          new Date(dateRange?.endDate)
        );
        setChartData(chartDataPoints);

        // NEW: Build readiness map for session history table
        // 1) Get unique dates from sessions
        const uniqueDates = [...new Set(data?.map(s => s?.date))];
        
        // 2) Fetch all wellness entries once
        const allWellness = await wellnessService?.getAll();
        
        // 3) Calculate readiness for all session dates
        const readinessByDateArray = calculateReadinessForDateRange(data, allWellness || [], uniqueDates);
        
        // 4) Convert array to map { [date]: readinessIndex }
        const newReadinessMap = {};
        readinessByDateArray?.forEach(item => {
          newReadinessMap[item?.date] = item?.readinessIndex;
        });
        
        setReadinessMap(newReadinessMap);
      } else {
        // No data - clear chart
        setChartData([]);
        setCurrentLoadStats(null);
        setLoadRatioTrendData(null);
        setReadinessMap({});
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
      setNotification({
        type: 'error',
        message: `Failed to load sessions: ${error?.message}`,
        duration: 5000
      });
    } finally {
      setIsLoading(false);
      setIsChartRefreshing(false);
    }
  };

  // NEW: Load today's wellness data
  const loadTodayWellnessData = async () => {
    if (!user) return;
    
    setIsLoadingWellness(true);
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const todayEntry = await wellnessService?.getByDate(today);
      setWellnessData(todayEntry);

      // Calculate wellness trend by comparing with yesterday
      const yesterday = new Date();
      yesterday?.setDate(yesterday?.getDate() - 1);
      const yesterdayStr = yesterday?.toISOString()?.split('T')?.[0];
      const yesterdayEntry = await wellnessService?.getByDate(yesterdayStr);

      if (todayEntry && yesterdayEntry) {
        const todayScore = todayEntry?.sleep + todayEntry?.fatigue + todayEntry?.soreness + todayEntry?.stress;
        const yesterdayScore = yesterdayEntry?.sleep + yesterdayEntry?.fatigue + yesterdayEntry?.soreness + yesterdayEntry?.stress;

        if (yesterdayScore > 0) {
          const percentChange = ((todayScore - yesterdayScore) / yesterdayScore) * 100;
          setWellnessTrend(Math.round(percentChange * 10) / 10);
        } else {
          setWellnessTrend(null);
        }
      } else {
        setWellnessTrend(null);
      }
    } catch (error) {
      console.error('Failed to load wellness data:', error);
      setWellnessData(null);
      setWellnessTrend(null);
    } finally {
      setIsLoadingWellness(false);
    }
  };

  // NEW: Load readiness index data
  const loadReadinessIndexData = async () => {
    if (!user) return;
    
    setIsLoadingReadiness(true);
    try {
      // Fetch all sessions and wellness entries
      const sessionsData = await sessionService?.getAll();
      let wellnessData = await wellnessService?.getAll();

      // Calculate readiness index
      const readinessData = calculateReadinessIndex(sessionsData || [], wellnessData || []);
      setReadinessIndexData(readinessData);
    } catch (error) {
      console.error('Failed to calculate readiness index:', error);
      setReadinessIndexData(null);
    } finally {
      setIsLoadingReadiness(false);
    }
  };

  // Remove old calculateLoadStats function - now using utility

  const getRiskCategory = (ratio) => {
    if (typeof ratio !== 'number') return 'low';
    if (ratio < 0.8) return 'low';
    if (ratio <= 1.3) return 'green';
    if (ratio <= 1.5) return 'amber';
    return 'red';
  };

  const getRiskStatus = (ratio) => {
    const category = getRiskCategory(ratio);
    switch (category) {
      case 'low': return 'green';
      case 'green': return 'success';
      case 'amber': return 'warning';
      case 'red': return 'error';
      default: return 'green';
    }
  };

  // Updated chart data generation using new utilities
  const refreshChartData = useMemo(() => {
    if (!sessions || sessions?.length === 0) return [];

    const chartDays = dateRange?.preset === 'custom'
      ? Math.ceil((new Date(dateRange.endDate) - new Date(dateRange.startDate)) / (24 * 60 * 60 * 1000)) + 1
      : parseInt(dateRange?.preset) || 30;

    return generateTrainingLoadChartData(
      sessions,
      chartDays,
      new Date(dateRange?.endDate)
    );
  }, [sessions, dateRange]);

  // Update chart data whenever refreshChartData changes
  useEffect(() => {
    setIsChartRefreshing(true);
    setChartData(refreshChartData);

    // Small delay to show refresh indicator
    const timer = setTimeout(() => {
      setIsChartRefreshing(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [refreshChartData]);

  // Fetch wellness data for chart overlay
  useEffect(() => {
    const fetchWellnessForChart = async () => {
      try {
        const allWellness = await wellnessService?.getAll();
        if (allWellness && allWellness?.length > 0) {
          // Format wellness data for chart
          const formattedWellness = allWellness?.map(entry => ({
            date: new Date(entry.date)?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
            hooperIndex: entry?.hooperIndex
          }));
          setWellnessChartData(formattedWellness);
        } else {
          setWellnessChartData([]);
        }
      } catch (error) {
        console.error('Failed to fetch wellness data for chart:', error);
        setWellnessChartData([]);
      }
    };

    fetchWellnessForChart();
  }, [sessions]); // Refresh when sessions change

  // NEW: Fetch readiness data for chart overlay
  useEffect(() => {
    const fetchReadinessForChart = async () => {
      try {
        const allSessions = await sessionService?.getAll();
        const allWellness = await wellnessService?.getAll();
        
        if (allSessions && allWellness && chartData?.length > 0) {
          // Extract dates from chartData
          const dates = chartData?.map(item => {
            // Convert from DD/MM format back to YYYY-MM-DD
            const [day, month] = item?.date?.split('/');
            const year = new Date()?.getFullYear();
            // Try current year first, then previous year if date is in future
            let dateObj = new Date(year, parseInt(month) - 1, parseInt(day));
            if (dateObj > new Date()) {
              dateObj = new Date(year - 1, parseInt(month) - 1, parseInt(day));
            }
            return dateObj?.toISOString()?.split('T')?.[0];
          });

          // Calculate readiness for each date
          const readinessData = calculateReadinessForDateRange(allSessions, allWellness, dates);
          
          // Format for chart (convert date back to DD/MM format)
          const formattedReadiness = readinessData?.map(entry => ({
            date: new Date(entry.date)?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
            readinessIndex: entry?.readinessIndex
          }));
          
          setReadinessChartData(formattedReadiness);
        } else {
          setReadinessChartData([]);
        }
      } catch (error) {
        console.error('Failed to calculate readiness data for chart:', error);
        setReadinessChartData([]);
      }
    };

    fetchReadinessForChart();
  }, [sessions, chartData]); // Refresh when sessions or chartData change

  // Update currentLoadStats whenever sessions change
  useEffect(() => {
    if (sessions?.length > 0) {
      const newStats = calculateCurrentLoadStats(sessions);
      setCurrentLoadStats(newStats);
      
      // Calculate load ratio trend data
      const trendData = calculateLoadRatioTrend(sessions);
      setLoadRatioTrendData(trendData);
    }
  }, [sessions]);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    setShowDatePicker(false);
    console.log('Date range changed - chart will auto-update:', newRange);
    // Chart will auto-update via useMemo dependency on dateRange
  };

  const handleExportData = async () => {
    try {
      setIsLoading(true);
      const count = await sessionService?.exportToCSV();
      setNotification({
        type: 'success',
        message: `Successfully exported ${count} training sessions to CSV`,
        duration: 5000
      });
    } catch (error) {
      console.error('Export failed:', error);
      setNotification({
        type: 'error',
        message: `Export failed: ${error?.message}`,
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportData = async (csvFile) => {
    try {
      setIsImporting(true);
      const result = await sessionService?.importFromCSV(csvFile);
      
      // Reload sessions after import
      await loadSessionsFromDatabase();
      
      // Show success message with import details
      let message = `Successfully imported ${result?.imported?.length} sessions`;
      if (result?.duplicates > 0) {
        message += `, skipped ${result?.duplicates} duplicates`;
      }
      if (result?.errors?.length > 0) {
        message += `, ${result?.errors?.length} errors`;
      }
      
      setNotification({
        type: 'success',
        message: message,
        duration: 5000
      });

      // Show errors if any
      if (result?.errors?.length > 0) {
        setTimeout(() => {
          setNotification({
            type: 'error',
            message: `Import errors: ${result?.errors?.slice(0, 3)?.join('; ')}${result?.errors?.length > 3 ? '...' : ''}`,
            duration: 10000
          });
        }, 2000);
      }

    } catch (error) {
      console.error('Import failed:', error);
      setNotification({
        type: 'error',
        message: `Import failed: ${error?.message}`,
        duration: 8000
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleRefreshData = async () => {
    await loadSessionsFromDatabase();
  };

  const handleSessionSubmit = async (sessionData) => {
    try {
      setIsLoading(true);
      
      // Use sessionService to create the session
      const newSession = await sessionService?.create(sessionData);
      
      // Reload all sessions to update the display and live chart
      await loadSessionsFromDatabase();
      await loadReadinessIndexData(); // NEW: Refresh readiness index

      setNotification({
        type: 'success',
        message: 'Session logged successfully! Training Load chart updated automatically.',
        duration: 5000
      });

    } catch (error) {
      console.error('Failed to save session:', error);
      setNotification({
        type: 'error',
        message: `Failed to save session: ${error?.message}`,
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSessionEdit = (session) => {
    setEditingSession(session);
    setIsEditModalOpen(true);
  };

  const handleSessionDelete = async (sessionId) => {
    try {
      setIsLoading(true);

      await sessionService?.delete(sessionId);

      // Reload sessions after deletion to update chart live
      await loadSessionsFromDatabase();

      setNotification({
        type: 'success',
        message: 'Session deleted successfully',
        duration: 3000
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Failed to delete session: ${error?.message}`,
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEdit = async (updatedSessionData) => {
    try {
      setIsLoading(true);

      // Update the session in the database
      await sessionService?.update(editingSession?.id, updatedSessionData);

      // Close modal
      setIsEditModalOpen(false);
      setEditingSession(null);

      // Reload sessions to update the display and chart
      await loadSessionsFromDatabase();
      await loadReadinessIndexData();

      setNotification({
        type: 'success',
        message: 'Session updated successfully',
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to update session:', error);
      setNotification({
        type: 'error',
        message: `Failed to update session: ${error?.message}`,
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingSession(null);
  };

  const handleSessionDeleteAll = async () => {
    try {
      setIsLoading(true);

      await sessionService?.deleteAll();

      // Reload sessions after deletion to update chart live
      await loadSessionsFromDatabase();

      setNotification({
        type: 'success',
        message: 'All sessions deleted successfully',
        duration: 3000
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Failed to delete all sessions: ${error?.message}`,
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-hide notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, notification?.duration);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Enhanced dynamic metrics using new calculation utilities
  const getDynamicMetrics = () => {
    // Calculate wellness score from today's data
    const wellnessScore = wellnessData
      ? wellnessData?.sleep + wellnessData?.fatigue + wellnessData?.soreness + wellnessData?.stress
      : null;

    // Calculate qualitative rating based on wellness score (FIXED: Lower is better)
    const getQualitativeRating = (score) => {
      if (!score) return null;
      if (score <= 12) return 'Excellent';
      if (score <= 16) return 'Good';
      if (score <= 20) return 'Fair';
      return 'Poor';
    };

    // Get qualitative guidance matching Hooper Index phrasing
    const getQualitativeGuidance = (score) => {
      if (!score) return null;
      if (score <= 12) return 'Optimal wellness state';
      if (score <= 16) return 'Good wellness state';
      if (score <= 20) return 'Monitor wellness closely';
      return 'Consider rest and recovery';
    };

    // NEW: Get Load Ratio qualitative descriptor
    const getLoadRatioQualitativeRating = (ratio) => {
      if (ratio === null || ratio === undefined) return null;
      if (ratio < 0.8) return 'Low Load';
      if (ratio <= 1.3) return 'Optimal Zone';
      if (ratio <= 1.5) return 'Elevated Risk';
      return 'High Risk';
    };

    // NEW: Calculate today's and yesterday's AU load
    const getTodayYesterdayLoads = () => {
      if (!sessions || sessions?.length === 0) return { today: null, yesterday: null };
      
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      
      const todayLoad = sessions
        ?.filter(s => s?.date === today)
        ?.reduce((sum, s) => sum + (s?.load || 0), 0);
      
      const yesterdayLoad = sessions
        ?.filter(s => s?.date === yesterday)
        ?.reduce((sum, s) => sum + (s?.load || 0), 0);
      
      return {
        today: todayLoad || 0,
        yesterday: yesterdayLoad || 0
      };
    };

    const auLoads = getTodayYesterdayLoads();

    return {
      acuteLoad: currentLoadStats ? { 
        value: currentLoadStats?.acuteLoad, 
        trend: 0, 
        status: 'primary' 
      } : { value: 'N/A', trend: 0, status: 'secondary' },
      chronicLoad: currentLoadStats ? { 
        value: currentLoadStats?.chronicLoad, 
        trend: 0, 
        status: 'primary' 
      } : { value: 'N/A', trend: 0, status: 'secondary' },
      loadRatio: currentLoadStats?.loadRatio ? { 
        value: currentLoadStats?.loadRatio, 
        trend: loadRatioTrendData?.dailyChangePercent || 0, // FIXED: Use actual daily change percent
        status: currentLoadStats?.riskBand?.category === 'low' || currentLoadStats?.riskBand?.category === 'low_load_risk' ? 'success' :
               currentLoadStats?.riskBand?.category === 'elevated' ? 'warning' :
               currentLoadStats?.riskBand?.category === 'high' ? 'error' : 'primary',
        qualitativeRating: getLoadRatioQualitativeRating(currentLoadStats?.loadRatio),
        todayAULoad: auLoads?.today,
        yesterdayAULoad: auLoads?.yesterday
      } : {
        value: 'N/A',
        trend: 0,
        status: 'secondary',
        qualitativeRating: null,
        todayAULoad: null,
        yesterdayAULoad: null
      },
      wellnessScore: {
        value: wellnessScore,
        hasData: !!wellnessData,
        breakdown: wellnessData ? {
          sleep: wellnessData?.sleep,
          fatigue: wellnessData?.fatigue,
          soreness: wellnessData?.soreness,
          stress: wellnessData?.stress
        } : null,
        trend: wellnessTrend || 0, // FIXED: Use calculated wellness trend
        status: wellnessScore ? (wellnessScore >= 20 ? 'success' : wellnessScore >= 14 ? 'warning' : 'error') : 'secondary',
        dailyChange: wellnessTrend || null,
        qualitativeRating: getQualitativeRating(wellnessScore),
        qualitativeGuidance: getQualitativeGuidance(wellnessScore)
      },
      readinessIndex: {
        value: readinessIndexData?.readinessIndex,
        hasData: readinessIndexData?.readinessIndex !== null,
        band: readinessIndexData?.readinessBand || 'insufficient_data',
        recommendation: readinessIndexData?.recommendation || 'Loading...',
        components: readinessIndexData?.components,
        states: readinessIndexData?.states,
        todayValues: readinessIndexData?.todayValues,
        status: readinessIndexData?.readinessBand === 'green' ? 'success' :
               readinessIndexData?.readinessBand === 'amber' ? 'warning' :
               readinessIndexData?.readinessBand === 'red'? 'error' : 'secondary'
      }
    };
  };

  const dynamicMetrics = getDynamicMetrics();

  const handleGenerateReport = async (reportConfig) => {
    setIsGeneratingPDF(true);
    try {
      // Fetch all sessions and wellness data
      const allSessions = await sessionService?.getAll();
      const allWellness = await wellnessService?.getAll();

      // Calculate summary metrics for the report date (end date)
      const reportDate = new Date(reportConfig.reportDate);
      const yesterday = new Date(reportDate);
      yesterday?.setDate(yesterday?.getDate() - 1);
      const yesterdayStr = yesterday?.toISOString()?.split('T')?.[0];
      const reportDateStr = reportDate?.toISOString()?.split('T')?.[0];

      // Calculate ACWR for report date
      const reportLoadStats = calculateCurrentLoadStats(allSessions, reportDate);
      
      // Calculate yesterday's ACWR for comparison
      const yesterdayLoadStats = calculateCurrentLoadStats(allSessions, yesterday);
      
      // Calculate daily load for today and yesterday
      const dailyLoads = calculateDailyLoads(allSessions);
      const todayAULoad = dailyLoads?.[reportDateStr] || 0;
      const yesterdayAULoad = dailyLoads?.[yesterdayStr] || 0;
      
      // Calculate percent change
      let dailyChangePercent = null;
      if (reportLoadStats?.loadRatio !== null && yesterdayLoadStats?.loadRatio !== null && yesterdayLoadStats?.loadRatio !== 0) {
        let dailyChange = reportLoadStats?.loadRatio - yesterdayLoadStats?.loadRatio;
        dailyChangePercent = ((dailyChange / yesterdayLoadStats?.loadRatio) * 100);
      }

      // Get wellness data for report date
      const reportWellness = allWellness?.find(w => w?.date === reportDateStr);
      const yesterdayWellness = allWellness?.find(w => w?.date === yesterdayStr);
      
      let wellnessData = null;
      if (reportWellness) {
        const hooperIndex = reportWellness?.hooper_index || 
          (reportWellness?.sleep + reportWellness?.fatigue + reportWellness?.soreness + reportWellness?.stress);
        
        // Calculate wellness qualitative rating based on Hooper Index
        let qualitativeRating = 'Fair';
        if (hooperIndex <= 12) qualitativeRating = 'Excellent';
        else if (hooperIndex <= 16) qualitativeRating = 'Good';
        else if (hooperIndex <= 20) qualitativeRating = 'Fair';
        else qualitativeRating = 'Poor';
        
        // Calculate daily change
        let dailyChange = null;
        if (yesterdayWellness) {
          const yesterdayHooper = yesterdayWellness?.hooper_index || 
            (yesterdayWellness?.sleep + yesterdayWellness?.fatigue + yesterdayWellness?.soreness + yesterdayWellness?.stress);
          dailyChange = ((hooperIndex - yesterdayHooper) / yesterdayHooper) * 100;
        }
        
        wellnessData = {
          sleep: reportWellness?.sleep,
          fatigue: reportWellness?.fatigue,
          soreness: reportWellness?.soreness,
          stress: reportWellness?.stress,
          hooperIndex: hooperIndex,
          qualitativeRating: qualitativeRating,
          dailyChange: dailyChange
        };
      }

      // Calculate readiness for report date
      // Note: calculateReadinessIndex calculates for "today" by default
      // We need to filter sessions/wellness to make report date appear as "today"
      const sessionsUpToReportDate = allSessions?.filter(s => new Date(s.date) <= reportDate);
      const wellnessUpToReportDate = allWellness?.filter(w => new Date(w.date) <= reportDate);
      const reportReadiness = calculateReadinessIndex(sessionsUpToReportDate, wellnessUpToReportDate);

      const summaryMetrics = {
        acwr: {
          value: reportLoadStats?.loadRatio,
          riskBand: reportLoadStats?.riskBand,
          qualitativeRating: reportLoadStats?.riskBand?.label,
          todayAULoad: todayAULoad,
          yesterdayAULoad: yesterdayAULoad
        },
        wellness: wellnessData,
        readiness: {
          value: reportReadiness?.readinessIndex,
          band: reportReadiness?.readinessBand,
          recommendation: reportReadiness?.recommendation,
          components: reportReadiness?.components,
          states: reportReadiness?.states,
          todayValues: reportReadiness?.todayValues,
          hasData: reportReadiness?.readinessIndex !== null
        }
      };

      // Generate 30-day chart data ending on report date
      const chartData30 = generateTrainingLoadChartData(allSessions, 30, reportDate);

      // Generate 90-day chart data ending on report date
      const chartData90 = generateTrainingLoadChartData(allSessions, 90, reportDate);

      // Generate chart data for selected range
      const rangeDays = Math.ceil((new Date(reportConfig.endDate) - new Date(reportConfig.startDate)) / (24 * 60 * 60 * 1000)) + 1;
      const chartDataRange = generateTrainingLoadChartData(allSessions, rangeDays, new Date(reportConfig.endDate));

      // Merge wellness data for all charts
      const mergeWellnessData = (chartData) => {
        const wellnessMap = {};
        allWellness?.forEach(entry => {
          const entryDate = new Date(entry.date)?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
          wellnessMap[entryDate] = entry?.hooperIndex;
        });
        return chartData?.map(item => ({
          ...item,
          wellness: wellnessMap?.[item?.date] || null
        }));
      };

      // Calculate readiness for all charts
      const mergeReadinessData = (chartData, referenceDate) => {
        const dates = chartData?.map(item => {
          const [day, month] = item?.date?.split('/');
          const year = referenceDate?.getFullYear();
          let dateObj = new Date(year, parseInt(month) - 1, parseInt(day));
          if (dateObj > referenceDate) {
            dateObj = new Date(year - 1, parseInt(month) - 1, parseInt(day));
          }
          return dateObj?.toISOString()?.split('T')?.[0];
        });

        const readinessDataArray = calculateReadinessForDateRange(allSessions, allWellness, dates);
        const readinessMap = {};
        readinessDataArray?.forEach(entry => {
          const entryDate = new Date(entry.date)?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
          readinessMap[entryDate] = entry?.readinessIndex;
        });

        return chartData?.map(item => ({
          ...item,
          readiness: readinessMap?.[item?.date] || null
        }));
      };

      const chartData30Final = mergeReadinessData(mergeWellnessData(chartData30), reportDate);
      const chartData90Final = mergeReadinessData(mergeWellnessData(chartData90), reportDate);
      const chartDataRangeFinal = mergeReadinessData(mergeWellnessData(chartDataRange), new Date(reportConfig.endDate));

      // Filter sessions for selected date range
      const sessionHistory = allSessions?.filter(s => s?.date >= reportConfig?.startDate && s?.date <= reportConfig?.endDate)?.sort((a, b) => new Date(b.date) - new Date(a.date))?.map(session => {
          // Calculate ACWR for this session date
          const sessionDate = new Date(session.date);
          const sessionLoadStats = calculateCurrentLoadStats(allSessions, sessionDate);

          // Get wellness for this date
          const sessionWellness = allWellness?.find(w => w?.date === session?.date);

          // Get readiness for this date
          const sessionReadiness = readinessMap?.[session?.date];

          return {
            date: session?.date,
            activityType: session?.activityType || 'N/A',
            minutes: session?.minutes,
            rpe: session?.rpe,
            load: session?.load,
            acwr: sessionLoadStats?.loadRatio || null,
            wellness: sessionWellness?.hooperIndex || null,
            readiness: sessionReadiness || null
          };
        });

      // Filter wellness history for selected date range
      const wellnessHistory = allWellness?.filter(w => w?.date >= reportConfig?.startDate && w?.date <= reportConfig?.endDate)?.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Set report data for rendering
      setReportData({
        reportConfig,
        summaryMetrics,
        chartData30: chartData30Final,
        chartData90: chartData90Final,
        chartDataRange: chartDataRangeFinal,
        sessionHistory,
        wellnessHistory,
        logoSrc: '/assets/images/Hub_LogoHI-1762941543093.png'
      });

      // Wait for ReportLayout to render
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate PDF
      await generatePDFReport(
        reportConfig,
        summaryMetrics,
        chartData30Final,
        chartData90Final,
        chartDataRangeFinal,
        sessionHistory,
        wellnessHistory
      );

      // Close modal and clear report data
      setIsReportModalOpen(false);
      setReportData(null);

      setNotification({
        type: 'success',
        message: 'PDF report generated successfully',
        duration: 5000
      });
    } catch (error) {
      console.error('Failed to generate report:', error);
      setNotification({
        type: 'error',
        message: `Failed to generate report: ${error?.message}`,
        duration: 5000
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Dashboard - Sports Science Platform</title>
        <meta name="description" content="Monitor your training load, wellness metrics, and performance trends with real-time analytics and insights." />
      </Helmet>
      <Header />
      <main className="pt-16 pb-24">
        {/* Dashboard Controls */}
        <DashboardControls
          onExportData={handleExportData}
          onImportData={handleImportData}
          onRefreshData={handleRefreshData}
          isLoading={isLoading}
          isImporting={isImporting}
        />

        <div className="p-6 space-y-6">
          {/* Training Logging Reminder Info Box */}
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg 
                  className="w-5 h-5 text-blue-600" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-text-primary mb-1">
                  Daily Training Log Reminder
                </h3>
                <p className="text-sm text-text-secondary leading-relaxed">
                  Please log every training session and sports participation, including PE lessons and any physical activity above your general day-to-day activity level. 
                  <span className="font-medium"> It's important to complete this every day</span> to ensure accurate data collection and predictive recommendations.
                </p>
              </div>
            </div>
          </div>

          {/* Notification */}
          {notification && (
            <div className={`p-4 rounded-lg border ${
              notification?.type === 'success' ?'bg-success/10 border-success/20 text-success'
                : notification?.type === 'error' ?'bg-error/10 border-error/20 text-error' :'bg-primary/10 border-primary/20 text-primary'
            }`}>
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium">{notification?.message}</span>
                <button
                  onClick={() => setNotification(null)}
                  className="ml-auto text-current hover:opacity-70"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Session Form */}
          <div className="space-y-6">
            <div className="grid grid-cols-1">
              <SessionForm 
                onSubmit={handleSessionSubmit}
              />
            </div>
          </div>

          {/* Download Report Button */}
          <div className="flex justify-end">
            <Button
              onClick={() => setIsReportModalOpen(true)}
              iconName="Download"
              iconPosition="left"
              variant="default"
            >
              Download Report
            </Button>
          </div>

          {/* Training Load Progression Explainer Box */}
          <div className="bg-muted border border-border rounded-lg p-5 shadow-sm">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg 
                  className="w-6 h-6 text-primary" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-text-primary mb-2">
                  Understanding Your Training Load Progression
                </h3>
                <div className="space-y-2 text-sm text-text-secondary">
                  <p className="leading-relaxed">
                    This chart visualizes your daily training load broken down by intensity (low, moderate, hard) and tracks your <span className="font-semibold">Load Ratio</span> over time.
                  </p>
                  <p className="leading-relaxed">
                    <span className="font-semibold text-text-primary">Key Focus: Load Ratio</span> - This is the critical metric for injury prevention. The Load Ratio compares your recent training (acute load) to your longer-term training (chronic load). An optimal ratio indicates you're building fitness safely, while elevated or high ratios may increase injury risk. Aim to keep your Load Ratio in the <span className="font-semibold">Optimal zone (0.8-1.3)</span> to ensure training load is being undertaken optimally and minimize injury risk.
                  </p>
                  <p className="leading-relaxed">
                    Optimal training is best achieved through <span className="font-semibold">progressive changes in athlete load</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Date Range Selector for Chart */}
          <div className="flex items-center justify-between bg-surface border border-border rounded-lg p-4">
            <div>
              <h3 className="text-sm font-semibold text-text-primary">Select Date Range</h3>
              <p className="text-xs text-text-secondary mt-1">Choose the time period to display in the Training Load Progression chart</p>
            </div>
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowDatePicker(!showDatePicker)}
                iconName="Calendar"
                iconPosition="left"
                className="whitespace-nowrap"
              >
                {dateRange?.preset === 'custom' 
                  ? `${dateRange?.startDate} to ${dateRange?.endDate}`
                  : `Last ${dateRange?.preset} days`
                }
              </Button>
              
              {showDatePicker && (
                <div className="absolute top-full right-0 mt-2 z-50">
                  <DateRangeSelector
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    onClose={() => setShowDatePicker(false)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Live Training Load Progression Chart */}
          <div className="grid grid-cols-1">
            <div className="relative">
              {isChartRefreshing && (
                <div className="absolute top-4 right-4 z-10">
                  <div className="bg-primary/10 border border-primary/20 text-primary px-3 py-1 rounded-full text-xs flex items-center space-x-2">
                    <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating chart...</span>
                  </div>
                </div>
              )}
              <LoadProgressionChart
                data={chartData}
                dateRange={dateRange}
                wellnessData={wellnessChartData}
                readinessData={readinessChartData}
              />
            </div>
          </div>

          {/* First Row: Load Ratio and Wellness Score */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <MetricCard
              title="Load Ratio (ACWR)"
              value={typeof currentLoadStats?.loadRatio === 'number' ? currentLoadStats?.loadRatio?.toFixed(2) : 'N/A'}
              unit=""
              trend={dynamicMetrics?.loadRatio?.trend}
              status={dynamicMetrics?.loadRatio?.status}
              icon="Target"
              description={currentLoadStats?.riskBand?.label || "Acute:Chronic ratio"}
              loadRiskCategory={currentLoadStats?.riskBand?.category || null}
              qualitativeSummary={loadRatioTrendData?.qualitativeSummary || null}
              dailyChange={loadRatioTrendData?.dailyChange || null}
              sevenDayTrend={loadRatioTrendData?.sevenDayTrend || null}
              wellnessBreakdown={null}
              hasWellnessData={false}
              isLoadingWellness={false}
              readinessData={null}
              isLoadingReadiness={false}
              loadRatioQualitativeRating={dynamicMetrics?.loadRatio?.qualitativeRating || null}
              todayAULoad={dynamicMetrics?.loadRatio?.todayAULoad}
              yesterdayAULoad={dynamicMetrics?.loadRatio?.yesterdayAULoad}
              dailyChangePercent={loadRatioTrendData?.dailyChangePercent || null}
            />
            <MetricCard
              title="Wellness Score"
              value={dynamicMetrics?.wellnessScore?.hasData 
                ? dynamicMetrics?.wellnessScore?.value 
                : 'No Data'}
              unit={dynamicMetrics?.wellnessScore?.hasData ? '/28' : ''}
              trend={dynamicMetrics?.wellnessScore?.trend}
              status={dynamicMetrics?.wellnessScore?.status}
              icon="Heart"
              description={dynamicMetrics?.wellnessScore?.hasData 
                ? "Total wellness score today" : "Complete wellness data to see score"}
              wellnessBreakdown={dynamicMetrics?.wellnessScore?.breakdown || null}
              hasWellnessData={dynamicMetrics?.wellnessScore?.hasData || false}
              isLoadingWellness={isLoadingWellness}
              loadRiskCategory={null}
              qualitativeSummary={dynamicMetrics?.wellnessScore?.qualitativeRating || null}
              dailyChange={dynamicMetrics?.wellnessScore?.dailyChange || null}
              sevenDayTrend={null}
              wellnessQualitativeRating={dynamicMetrics?.wellnessScore?.qualitativeRating || null}
              wellnessQualitativeGuidance={dynamicMetrics?.wellnessScore?.qualitativeGuidance || null}
              readinessData={null}
              isLoadingReadiness={false}
            />
          </div>

          {/* Second Row: Readiness Index */}
          <div className="grid grid-cols-1">
            <MetricCard
              title="Readiness Index"
              value={dynamicMetrics?.readinessIndex?.hasData 
                ? dynamicMetrics?.readinessIndex?.value?.toFixed(2) 
                : 'N/A'}
              unit=""
              trend={0}
              status={dynamicMetrics?.readinessIndex?.status}
              icon="Activity"
              description={dynamicMetrics?.readinessIndex?.band === 'green' ? 'Good to go' :
                          dynamicMetrics?.readinessIndex?.band === 'amber' ? 'Proceed with caution' :
                          dynamicMetrics?.readinessIndex?.band === 'red'? 'High strain/risk' : 'Insufficient data'}
              readinessData={dynamicMetrics?.readinessIndex}
              isLoadingReadiness={isLoadingReadiness}
              loadRiskCategory={null}
              qualitativeSummary={null}
              dailyChange={null}
              sevenDayTrend={null}
              wellnessBreakdown={null}
              hasWellnessData={false}
              isLoadingWellness={false}
            />
          </div>

          {/* Session History Table with live updates */}
          <SessionHistoryTable
            sessions={sessions}
            onEdit={handleSessionEdit}
            onDelete={handleSessionDelete}
            onDeleteAll={handleSessionDeleteAll}
            onExport={handleExportData}
            onImport={handleImportData}
            isLoading={isLoading}
            isImporting={isImporting}
            readinessMap={readinessMap}
          />
        </div>
      </main>
      <ContactButton />
      {/* Edit Session Modal */}
      {isEditModalOpen && editingSession && (
        <EditSessionModal
          session={editingSession}
          onSave={handleSaveEdit}
          onClose={handleCloseEditModal}
          isLoading={isLoading}
        />
      )}
      {/* Download Report Modal */}
      {isReportModalOpen && (
        <DownloadReportModal
          onClose={() => setIsReportModalOpen(false)}
          onGenerate={handleGenerateReport}
          isGenerating={isGeneratingPDF}
        />
      )}
      {/* Report Layout (hidden, for PDF generation) */}
      {reportData && (
        <ReportLayout
          reportConfig={reportData?.reportConfig}
          summaryMetrics={reportData?.summaryMetrics}
          chartData30={reportData?.chartData30}
          chartData90={reportData?.chartData90}
          chartDataRange={reportData?.chartDataRange}
          sessionHistory={reportData?.sessionHistory}
          wellnessHistory={reportData?.wellnessHistory}
          logoSrc={reportData?.logoSrc}
          allSessions={reportData?.sessions}
        />
      )}
    </div>
  );
};

export default DashboardOverview;